/**
 * Employment Group API Contract
 *
 * Defines the oRPC contract for SmartGroups - auto-populated employee groups.
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateEmploymentGroupInputSchema, UpdateEmploymentGroupInputSchema, AddGroupMembersInputSchema, AvailableRuleFieldsResponseSchema, EmploymentGroupResponseSchema, EmploymentGroupDetailResponseSchema, ListEmploymentGroupsResponseSchema, ApplyRulesResponseSchema, AddMembersResponseSchema, RemoveMemberResponseSchema, } from "@hourly/api/employmentGroup";
import { uuidSchema } from "@hourly/api/validation-common";
// ============ Contract Definitions ============
// Create an employment group
export const createEmploymentGroup = oc
    .route({
    method: "POST",
    path: "/employment-groups",
    summary: "Create a new employment group (SmartGroup or Manual)",
    tags: ["EmploymentGroups"],
    successStatus: 201,
})
    .input(CreateEmploymentGroupInputSchema)
    .output(EmploymentGroupResponseSchema);
// Get an employment group by ID
export const getEmploymentGroup = oc
    .route({
    method: "GET",
    path: "/employment-groups/{id}",
    summary: "Get an employment group by ID",
    tags: ["EmploymentGroups"],
})
    .input(z.object({ id: uuidSchema }))
    .output(EmploymentGroupResponseSchema);
// Get an employment group with members
export const getEmploymentGroupWithMembers = oc
    .route({
    method: "GET",
    path: "/employment-groups/{id}/members",
    summary: "Get an employment group with its members",
    tags: ["EmploymentGroups"],
})
    .input(z.object({ id: uuidSchema }))
    .output(EmploymentGroupDetailResponseSchema);
// List employment groups for a company
export const listEmploymentGroups = oc
    .route({
    method: "GET",
    path: "/employment-groups",
    summary: "List employment groups for a company",
    tags: ["EmploymentGroups"],
})
    .input(z.object({
    limit: z.coerce.number().min(1).max(100).optional().default(100),
    offset: z.coerce.number().min(0).optional().default(0),
}))
    .output(ListEmploymentGroupsResponseSchema);
// Update an employment group
export const updateEmploymentGroup = oc
    .route({
    method: "PUT",
    path: "/employment-groups/{id}",
    summary: "Update an employment group",
    tags: ["EmploymentGroups"],
})
    .input(z.object({ id: uuidSchema }).merge(UpdateEmploymentGroupInputSchema))
    .output(EmploymentGroupResponseSchema);
// Delete an employment group
export const deleteEmploymentGroup = oc
    .route({
    method: "DELETE",
    path: "/employment-groups/{id}",
    summary: "Delete an employment group",
    tags: ["EmploymentGroups"],
    successStatus: 204,
})
    .input(z.object({ id: uuidSchema }))
    .output(z.void());
// Apply rules to an employment group (re-evaluate and update members)
export const applyEmploymentGroupRules = oc
    .route({
    method: "POST",
    path: "/employment-groups/{id}/apply-rules",
    summary: "Apply rules to an employment group and update members",
    tags: ["EmploymentGroups"],
})
    .input(z.object({ id: uuidSchema }))
    .output(ApplyRulesResponseSchema);
// Get available rule fields for a company
export const getAvailableRuleFields = oc
    .route({
    method: "GET",
    path: "/employment-groups/rule-fields",
    summary: "Get available fields and values for employment group rules",
    tags: ["EmploymentGroups"],
})
    .input(z.object({}))
    .output(AvailableRuleFieldsResponseSchema);
// ============ Manual Group Member Management ============
// Add members to a manual group
export const addGroupMembers = oc
    .route({
    method: "POST",
    path: "/employment-groups/{id}/members",
    summary: "Add members to a manual employment group",
    tags: ["EmploymentGroups"],
})
    .input(z.object({ id: uuidSchema }).merge(AddGroupMembersInputSchema))
    .output(AddMembersResponseSchema);
// Remove a member from a manual group
export const removeGroupMember = oc
    .route({
    method: "DELETE",
    path: "/employment-groups/{id}/members/{employmentId}",
    summary: "Remove a member from a manual employment group",
    tags: ["EmploymentGroups"],
})
    .input(z.object({ id: uuidSchema, employmentId: uuidSchema }))
    .output(RemoveMemberResponseSchema);
// ============ Export Contract ============
export const employmentGroupContract = {
    create: createEmploymentGroup,
    get: getEmploymentGroup,
    getWithMembers: getEmploymentGroupWithMembers,
    list: listEmploymentGroups,
    update: updateEmploymentGroup,
    delete: deleteEmploymentGroup,
    applyRules: applyEmploymentGroupRules,
    getAvailableRuleFields,
    addMembers: addGroupMembers,
    removeMember: removeGroupMember,
};
//# sourceMappingURL=employmentGroup.js.map